import type { DeepReadonly } from 'vue';
import type { FormField } from './form';

/**
 * One entry in a unit's `conversions`: a unit, and the formula that takes a number in that unit into this one. Only
 * `*` and `/` are written.
 */
export type UnitConversion = [unit: string, formula: [operator: string, factor: number]];

/** One of the units a unit field offers. */
export type FieldUnit = DeepReadonly<NonNullable<FormField['options']['units']>['list'][number]>;

const applyFormula = ([operator, factor]: DeepReadonly<UnitConversion[1]>, value: number) => {
  if (operator === '*') {
    return value * factor;
  }
  return operator === '/' ? value / factor : value;
};

/** The formula in `into`'s conversions that takes a number in `from` into `into`. */
const formulaInto = (into: FieldUnit, from: string | undefined) =>
  (into.conversions ?? []).find(([unit]) => unit === from)?.[1];

const defaultUnitOf = (field: DeepReadonly<FormField>) => {
  const units = field.options.units;
  return units?.list.find(unit => unit.id === units.default);
};

/** `value`, a number in `from`, in `into`: as it is where they are one unit, null where `into` has no formula. */
const convertedInto = (into: FieldUnit, from: string | undefined, value: number) => {
  if (into.unit === from) {
    return value;
  }
  const formula = formulaInto(into, from);
  return formula ? applyFormula(formula, value) : null;
};

/**
 * A unit field's number in its default unit, which is how conditions and message references read it. A number of 0
 * or none is left as it is, and so is one on a field with no default unit, or no formula from the field's unit.
 */
export const numberInDefaultUnit = (field: DeepReadonly<FormField>) => {
  const value = field.numberValue;
  const defaultUnit = defaultUnitOf(field);
  if (!value || !defaultUnit) {
    return value;
  }
  return convertedInto(defaultUnit, field.value, value) ?? value;
};

/**
 * A unit field's number in `to`: into the default unit first, then from there into `to`, which is what a unit pick
 * writes. Null where either step has no formula, the field's unit into the default or the default into `to`, so the
 * pick clears a number it cannot convert rather than keep it under a unit it was not typed in (ENG-9239). A number of 0
 * or none is left as it is, and so is one on a field with no default unit to convert through.
 */
export const numberInUnit = (field: DeepReadonly<FormField>, to: FieldUnit) => {
  const value = field.numberValue;
  const defaultUnit = defaultUnitOf(field);
  if (!value || !defaultUnit) {
    return value;
  }
  const inDefaultUnit = convertedInto(defaultUnit, field.value, value);
  return inDefaultUnit === null ? null : convertedInto(to, defaultUnit.unit, inDefaultUnit);
};
